import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useCanGoBack,
	useNavigate,
	useRouter,
} from "@tanstack/react-router";
import { Trash2, X } from "lucide-react";
import { useState } from "react";
import { ContactAIIntelligence } from "@/components/contacts/contact-ai-intelligence";
import { ContactCallHistorySection } from "@/components/contacts/contact-call-history-section";
import { ContactDeleteDialog } from "@/components/contacts/contact-delete-dialog";
import { ContactDonationsSection } from "@/components/contacts/contact-donations-section";
import { ContactEnrichmentSection } from "@/components/contacts/contact-enrichment-section";
import { ContactFollowUpsSection } from "@/components/contacts/contact-follow-ups-section";
import { ContactInfoSection } from "@/components/contacts/contact-info-section";
import { ContactInteractionsSection } from "@/components/contacts/contact-interactions-section";
import { ContactMetadataSection } from "@/components/contacts/contact-metadata-section";
import { ContactPhoneHistorySection } from "@/components/contacts/contact-phone-history-section";
import { ContactTagsSection } from "@/components/contacts/contact-tags-section";
import { ContactTextInteractionsSection } from "@/components/contacts/contact-text-interactions-section";
import type { ListOrigin } from "@/components/voter-file/voter-file-search-params";
import { Button } from "@/components/ui/button";
import { ReadWriteOnly } from "@/components/common/read-write-only";
import { Spinner } from "@/components/ui/spinner";
import { useSession } from "@/hooks/use-session";
import { apiService } from "@/lib/api-service";

export const Route = createFileRoute("/_app/_/contacts/$contactId")({
	component: ContactDetailPage,
	// `from` records which list the user opened this contact from (forwarded
	// by the voter-file detail redirect) so Close returns there.
	validateSearch: (search: Record<string, unknown>): { from?: ListOrigin } =>
		search.from === "/your-voters" || search.from === "/contacts"
			? { from: search.from }
			: {},
});

function ContactDetailPage() {
	const { contactId } = Route.useParams();
	const { from } = Route.useSearch();
	const contactIdNum = Number(contactId);
	const navigate = useNavigate();
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const session = useSession();
	const campaignId = session.campaign?.id;
	const [deleteOpen, setDeleteOpen] = useState(false);

	/**
	 * Return to the originating list. history.back() preserves that list's
	 * filter/sort/page state (which /your-voters mirrors into its URL);
	 * `from` is the fallback target for deep links with no history to pop.
	 *
	 * Deletion deliberately shares this handler: after a delete the previous
	 * entry is the list, and it refetches on mount, so the removed row is
	 * gone without needing a different destination.
	 */
	const handleClose = () => {
		// `from` is set ONLY by a list row click, so its presence is what
		// proves the previous history entry is the list. canGoBack alone just
		// means "some history exists" — a user arriving from the dashboard, a
		// notification, or an external referrer would get bounced somewhere
		// unrelated. With both, back() restores the list's filter/sort/page
		// (mirrored into its URL); otherwise navigate to a known list.
		if (from && canGoBack) {
			router.history.back();
			return;
		}
		void navigate({ to: from ?? "/contacts" });
	};

	const {
		data: contact,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["contacts", campaignId, contactIdNum],
		queryFn: () => apiService.getContact(campaignId!, contactIdNum),
		enabled: !!campaignId,
	});

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl">
					<p className="text-muted-foreground">
						No campaign found. Please complete onboarding first.
					</p>
				</div>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl flex items-center justify-center py-20">
					<Spinner className="size-6" />
				</div>
			</div>
		);
	}

	if (error || !contact) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl space-y-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={handleClose}
					>
						<X data-icon="inline-start" className="size-4" />
						Close
					</Button>
					<div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
						<p className="text-destructive">
							Failed to load contact. It may have been deleted.
						</p>
					</div>
				</div>
			</div>
		);
	}

	const contactName =
		contact.full_name ||
		[contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
		"Unnamed Contact";

	const enrichment = contact.voter_enrichment;

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-7xl space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<h1 className="text-2xl font-semibold">{contactName}</h1>
					<div className="flex items-center gap-1">
						<ReadWriteOnly message="Read-only — you can't delete contacts"><Button
							variant="ghost"
							size="icon-sm"
							onClick={() => setDeleteOpen(true)}
							aria-label="Delete contact"
							className="text-destructive hover:text-destructive"
						>
							<Trash2 className="size-5" />
						</Button></ReadWriteOnly>
						<Button
							variant="ghost"
							size="icon-sm"
							onClick={handleClose}
							aria-label="Close contact detail"
						>
							<X className="size-5" />
						</Button>
					</div>
				</div>

				<ContactDeleteDialog
					open={deleteOpen}
					onOpenChange={setDeleteOpen}
					campaignId={campaignId}
					contactId={contactIdNum}
					contactName={contactName}
					onDeleted={handleClose}
				/>

				{/* Two-panel grid */}
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
					{/* Left panel */}
					<div className="space-y-6 lg:col-span-2 order-2 lg:order-1">
						<ContactAIIntelligence contact={contact} campaignId={campaignId} />
						{session.campaign?.voter_file_enrichment_access && (
							<ContactEnrichmentSection
								enrichment={enrichment}
								campaignId={campaignId}
								contactId={contactIdNum}
							/>
						)}
					</div>

					{/* Right panel */}
					<div className="space-y-6 lg:col-span-3 order-1 lg:order-2">
						<ContactInfoSection contact={contact} campaignId={campaignId} />
						<ContactMetadataSection contact={contact} campaignId={campaignId} />
						<ContactTagsSection contact={contact} campaignId={campaignId} />
						<ContactFollowUpsSection
							contactId={contactIdNum}
							contactName={contactName}
							campaignId={campaignId}
						/>
						<ContactInteractionsSection
							contactId={contactIdNum}
							campaignId={campaignId}
						/>
						<ContactCallHistorySection
							contactId={contactIdNum}
							campaignId={campaignId}
						/>
						<ContactTextInteractionsSection
							contactId={contactIdNum}
							campaignId={campaignId}
							contact={contact}
						/>
						<ContactPhoneHistorySection
							contactId={contactIdNum}
							campaignId={campaignId}
						/>
						<ContactDonationsSection
							contactId={contactIdNum}
							campaignId={campaignId}
							contactName={contactName}
							contactEmail={contact.email ?? undefined}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
